import type {
  CreateWorkAreaMutationVariables,
  DeleteWorkAreaMutationVariables,
  UpdateWorkAreaMutationVariables,
} from "@/types/gql/graphql";
import {
  QUERY_ADD_WORK_AREA,
  QUERY_REMOVE_WORK_AREA,
  QUERY_UPDATE_WORK_AREA,
  QUERY_WORK_AREAS,
} from "@/services/web/api-work-area.const";
import { Feature, Polygon } from "geojson";
import { useContext, useMemo } from "react";
import { useTrackedMutation, useTrackedQuery } from "@/hooks/apollo-insights-hooks";

import { CityContext } from "@/contexts/city-context-provider";
import { showNotification } from "@/components/notification";

export const useQueryWorkArea = () => {
  const { currentCity } = useContext(CityContext);

  const variables = useMemo(() => {
    return {
      cityId: currentCity?.id ?? "",
    };
  }, [currentCity?.id]);

  const refetchQueriesConfig = useMemo(() => [{ query: QUERY_WORK_AREAS, variables }], [variables]);

  const { data, loading } = useTrackedQuery(QUERY_WORK_AREAS, {
    actionName: "FindWorkAreas",
    variables,
    skip: !currentCity?.id,
  });

  const [createWorkArea] = useTrackedMutation(QUERY_ADD_WORK_AREA, {
    actionName: "CreateWorkArea",
    refetchQueries: refetchQueriesConfig,
    awaitRefetchQueries: true,
    onError: (error) => {
      showNotification({
        variant: "error",
        message: error.message,
      });
    },
  });

  const [updateWorkArea] = useTrackedMutation(QUERY_UPDATE_WORK_AREA, {
    actionName: "UpdateWorkArea",
    refetchQueries: refetchQueriesConfig,
    awaitRefetchQueries: true,
    onError: (error) => {
      showNotification({
        variant: "error",
        message: error.message,
      });
    },
  });

  const [removeWorkArea] = useTrackedMutation(QUERY_REMOVE_WORK_AREA, {
    actionName: "DeleteWorkArea",
    refetchQueries: refetchQueriesConfig,
    awaitRefetchQueries: true,
    onError: (error) => {
      showNotification({
        variant: "error",
        message: error.message,
      });
    },
  });

  const handleAddWorkArea = async (variables: CreateWorkAreaMutationVariables) => {
    const response = await createWorkArea({ variables });
    return response.data;
  };

  const handleUpdateWorkArea = async (variables: UpdateWorkAreaMutationVariables) => {
    if ("type" in variables.workAreaInput) {
      delete variables.workAreaInput.type;
    }

    if ("properties" in variables.workAreaInput) {
      delete variables.workAreaInput.properties;
    }
    const response = await updateWorkArea({ variables });
    return response.data;
  };

  const handleRemoveWorkArea = async (variables: DeleteWorkAreaMutationVariables) => {
    const response = await removeWorkArea({ variables });
    return response.data;
  };

  const workAreas: Feature<Polygon>[] = useMemo(() => {
    return (
      data?.workAreas.map((workArea) => ({
        ...workArea,
        type: "Feature",
        properties: {},
      })) ?? []
    );
  }, [data?.workAreas]);

  return {
    workAreas: workAreas,
    loading,
    handleAddWorkArea,
    handleUpdateWorkArea,
    handleRemoveWorkArea,
  };
};
